import React, { VFC } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import {
  Theme,
  LabelValue,
  List,
  ListItem,
  makeStyles,
  CardHeading,
  Box,
  Chip,
} from '@dxb-web-ui-framework/design-system';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useOrderStatuses } from '../../../contexts/Parameters/hooks';
import { ConsolidatedTransactionDetails } from '../../../domain/Transaction/entities/ConsolidatedTransaction';
import { TransactionOrderStatusChip } from '../../TransactionOrderStatusChip';
import { OrderChargesResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/OrderChargesResponseDTO';
import { useTransactionCharges } from '../../../utils';

type ConsolidatedTransactionDetailsListProps = {
  transactionDetails: ConsolidatedTransactionDetails;
  isStandalonePage?: boolean;
  isLoading: boolean;
  orderCharges?: OrderChargesResponseBody;
  error?: any;
};

const useStyles = makeStyles<Theme, { isStandalonePage?: boolean }>(theme => ({
  detailsBox: {
    marginTop: ({ isStandalonePage }) => (isStandalonePage ? theme.spacing(7) : theme.spacing(0)),
    height: ({ isStandalonePage }) => (isStandalonePage ? '' : '100%'),
  },
  detailsList: {
    paddingLeft: ({ isStandalonePage }) => (isStandalonePage ? theme.spacing(3) : theme.spacing(5)),
    paddingTop: ({ isStandalonePage }) => (isStandalonePage ? theme.spacing(3) : theme.spacing(5)),
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(0),
    },
  },
  detailsListItem: {
    paddingLeft: theme.spacing(0),
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(3),
  },
}));

const TransactionDetailsList: VFC<ConsolidatedTransactionDetailsListProps> = ({
  transactionDetails,
  isStandalonePage,
  isLoading,
  orderCharges,
  error,
}) => {
  const { t } = useTranslation();
  const orderStatuses = useOrderStatuses() ?? [];
  const classes = useStyles({
    isStandalonePage,
  });

  const labels: GenericObject = {
    orderId: t('fundsTransferPage.TransferFlow.TransactionId'),
    accountCurrency: t('fundsTransferPage.Forms.Currency'),
    exchangeCurrency: t('fundsTransferPage.Forms.Currency'),
    status: t('fundsTransferPage.TransferFlow.Status'),
    type: t('fundsTransferPage.TransferFlow.Type'),
    commission: t('fundsTransferPage.TransferFlow.Commission'),
    totalCharges: t('fundsTransferPage.TransferFlow.TransferTotalCharges'),
    charges: t('fundsTransferPage.TransferFlow.Charges'),
    beneficiaryFullName:
      transactionDetails?.type === 'O'
        ? t('fundsTransferPage.TransferFlow.Beneficiary.FullName')
        : t('fundsTransferPage.TransferFlow.Counterparty.FullName'),
    beneficiaryCountry:
      transactionDetails?.type === 'O'
        ? t('fundsTransferPage.TransferFlow.BeneficiaryCountry')
        : t('fundsTransferPage.TransferFlow.CounterpartyCountry'),
    bankName:
      transactionDetails?.type === 'O'
        ? t('fundsTransferPage.TransferFlow.BenBankName')
        : t('fundsTransferPage.TransferFlow.CounterpartyBankName'),
    bankBic:
      transactionDetails?.type === 'O'
        ? t('fundsTransferPage.TransferFlow.BankBIC')
        : t('fundsTransferPage.TransferFlow.CounterpartyBankBIC'),
    beneficiaryAddress:
      transactionDetails?.type === 'O'
        ? t('fundsTransferPage.TransferFlow.BeneficiaryAddress')
        : t('fundsTransferPage.TransferFlow.CounterpartyAddress'),
    beneficiaryBankCountry:
      transactionDetails?.type === 'O'
        ? t('fundsTransferPage.TransferFlow.BeneficiaryBankCountry')
        : t('fundsTransferPage.TransferFlow.CounterpartyBankCountry'),
    beneficiaryCity:
      transactionDetails?.type === 'O'
        ? t('fundsTransferPage.TransferFlow.BeneficiaryCity')
        : t('fundsTransferPage.TransferFlow.CounterpartyCity'),
    details: t('fundsTransferPage.TransferFlow.Comments'),
    cancellationReason: t('fundTransferDetailsLista.cancellationReason'),
    bop: t('fundsTransferPage.TransferFlow.BopCode'),
    sortCode: t('fundsTransferPage.TransferFlow.SortCode'),
    cor: t('fundsTransferPage.TransferFlow.COR'),
    bicBankCode: t('fundsTransferPage.TransferFlow.BIK'),
    inn: t('fundsTransferPage.TransferFlow.INN'),
    kpp: t('fundsTransferPage.TransferFlow.KPP'),
    voDescription: t('fundsTransferPage.TransferFlow.VO'),
  };

  const { commisionAmount, totalCharges } = useTransactionCharges(transactionDetails, orderCharges, error);

  return (
    <Box className={classes.detailsBox}>
      {isStandalonePage && (
        <Box ml={3}>
          <CardHeading
            heading={t(
              'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.TransactionDetailsList.Heading',
            )}
            hideOnMobile={false}
          />
        </Box>
      )}
      <List className={classes.detailsList}>
        <ListItem className={classes.detailsListItem}>
          <LabelValue
            label={labels.orderId}
            value={transactionDetails?.orderId}
            valueAreaLabel={`${labels.orderId} ${transactionDetails?.orderId}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize='small'
            valueColor='secondary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </ListItem>
        <ListItem className={classes.detailsListItem}>
          <LabelValue
            label={labels.type}
            value={
              <Chip
                label={
                  transactionDetails?.type === 'O'
                    ? t('Transaction.Transfer.Debit.Type')
                    : t('Transaction.Transfer.Credit.Type')
                }
                bgColor={transactionDetails?.type === 'O' ? 'error' : 'success'}
              />
            }
            valueAreaLabel={`${labels.type} ${
              transactionDetails?.type === 'O'
                ? t('Transaction.Transfer.Debit.Type')
                : t('Transaction.Transfer.Credit.Type')
            }`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize='small'
            valueColor='secondary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </ListItem>
        <ListItem className={classes.detailsListItem}>
          <LabelValue
            label={labels.status}
            value={
              transactionDetails ? (
                <TransactionOrderStatusChip statusSerialNumber={Number(transactionDetails?.status)} />
              ) : (
                '-'
              )
            }
            valueAreaLabel={`${labels.status} ${
              (transactionDetails &&
                !isEmpty(orderStatuses) &&
                orderStatuses.find(os => os.serialNumber === Number(transactionDetails?.status))?.description) ??
              transactionDetails?.status ??
              '-'
            }`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize='small'
            valueColor='secondary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </ListItem>
        {transactionDetails?.type === 'O' && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.commission}
              value={commisionAmount}
              valueAreaLabel={commisionAmount}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.type === 'O' && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.totalCharges}
              value={totalCharges}
              valueAreaLabel={totalCharges}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.accountCurrency && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.accountCurrency}
              value={transactionDetails?.accountCurrency}
              valueAreaLabel={`${labels.accountCurrency} ${transactionDetails?.accountCurrency}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.charges !== 'DEFAULT' && transactionDetails?.type === 'O' && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.charges}
              value={transactionDetails?.charges}
              valueAreaLabel={`${labels.charges} ${transactionDetails?.charges}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        <ListItem className={classes.detailsListItem}>
          <LabelValue
            label={labels.beneficiaryFullName}
            value={transactionDetails?.beneficiaryFullName}
            valueAreaLabel={`${labels.beneficiaryFullName} ${transactionDetails?.beneficiaryFullName}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize='small'
            valueColor='secondary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </ListItem>
        <ListItem className={classes.detailsListItem}>
          <LabelValue
            label={labels.bankName}
            value={transactionDetails?.bankName}
            valueAreaLabel={`${labels.bankName} ${transactionDetails?.bankName}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize='small'
            valueColor='secondary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </ListItem>
        <ListItem className={classes.detailsListItem}>
          <LabelValue
            label={labels.bankBic}
            value={transactionDetails?.bankBic}
            valueAreaLabel={`${labels.bankBic} ${transactionDetails?.bankBic}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize='small'
            valueColor='secondary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </ListItem>
        {transactionDetails?.details && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.details}
              value={transactionDetails?.details}
              valueAreaLabel={`${labels.details} ${transactionDetails?.details}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.bop && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.bop}
              value={transactionDetails?.bop}
              valueAreaLabel={`${labels.bop} ${transactionDetails?.bop}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.sortCode && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.sortCode}
              value={transactionDetails?.sortCode}
              valueAreaLabel={`${labels.sortCode} ${transactionDetails?.sortCode}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.cor && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.cor}
              value={transactionDetails?.cor}
              valueAreaLabel={`${labels.cor} ${transactionDetails?.cor}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.bicBankCode && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.bicBankCode}
              value={transactionDetails?.bicBankCode}
              valueAreaLabel={`${labels.bicBankCode} ${transactionDetails?.bicBankCode}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.inn && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.inn}
              value={transactionDetails?.inn}
              valueAreaLabel={`${labels.inn} ${transactionDetails?.inn}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.kpp && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.kpp}
              value={transactionDetails?.kpp}
              valueAreaLabel={`${labels.kpp} ${transactionDetails?.kpp}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
        {transactionDetails?.voDescription && (
          <ListItem className={classes.detailsListItem}>
            <LabelValue
              label={labels.voDescription}
              value={transactionDetails?.voDescription}
              valueAreaLabel={`${labels.voDescription} ${transactionDetails?.voDescription}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize='small'
              valueColor='secondary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );
};

export { TransactionDetailsList };
